import { useCallback, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { listCustomersOptions } from '@/client/@tanstack/react-query.gen'
import type { CustomerSummary } from '@/client/types.gen'
import { useCustomerId } from '@/components/customer-id-provider'
import { Combobox } from '@/components/ui/combobox'
import { useAuth } from '@/hooks/useAuth'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { cn } from '@/lib/utils'

export function CustomerSelect() {
  const { t } = useTranslation()
  const { isCustomerUser, isLoading } = useAuth()
  const { customerId, setCustomerId } = useCustomerId()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)

  const { data: customers, isLoading: customersLoading } = useQuery({
    ...listCustomersOptions({
      query: debouncedSearch ? { search: debouncedSearch } : undefined,
    }),
    enabled: !isCustomerUser && !isLoading,
    placeholderData: keepPreviousData,
  })

  const handleChange = useCallback(
    (id: string) => {
      setCustomerId(id || null)
    },
    [setCustomerId],
  )

  if (isCustomerUser) return null

  const authReady = !isLoading
  const items = authReady
    ? (customers ?? []).map((c: CustomerSummary) => ({
        value: c.id,
        label: c.name,
      }))
    : []

  return (
    <div
      className={cn(
        'flex max-w-xl flex-wrap items-center gap-2 sm:flex-nowrap',
      )}
    >
      {authReady && customersLoading && (
        <Loader2
          className="text-muted-foreground size-4 shrink-0 animate-spin"
          aria-hidden
        />
      )}
      <Combobox
        disabled={!authReady}
        items={items}
        value={authReady ? (customerId ?? '') : ''}
        onChange={handleChange}
        placeholder={
          authReady ? t('customer.selectPlaceholder') : t('customer.loadingPlaceholder')
        }
        emptyText={t('customer.emptyText')}
        searchPlaceholder={t('customer.searchPlaceholder')}
        searchValue={search}
        onSearchChange={setSearch}
        getLabel={(item) => item.label}
        className="min-w-64"
      />
    </div>
  )
}
